import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'

/** The screen is laid out at this height, then scaled to the window, so nothing is ever clipped. */
export const STAGE_HEIGHT = 810
/** Narrowest and widest layout width: a 4:3 or an ultrawide TV gets bars rather than a squeezed layout. */
export const STAGE_MIN_WIDTH = 1100
export const STAGE_MAX_WIDTH = 2000
/** Below this window width the screen is a normal scrolling page (a phone), not a TV. */
export const STAGE_BREAKPOINT = 900

export interface StageFit {
  width: number
  scale: number
  left: number
  top: number
}

/**
 * Where the stage goes in a window (Kyle, 2026-10-02: "make it all fit on one page"). The layout is drawn
 * at a fixed design height and a width that follows the window's shape, then scaled to fill the window:
 * the same page fits a 720p, 1080p or 4K TV, and what fits at the design size cannot clip at any size.
 * Null on a phone-width window, where the page just scrolls.
 */
export function stageFit(vw: number, vh: number): StageFit | null {
  if (vw < STAGE_BREAKPOINT || vh <= 0) return null
  const width = Math.min(STAGE_MAX_WIDTH, Math.max(STAGE_MIN_WIDTH, Math.round((vw / vh) * STAGE_HEIGHT)))
  const scale = Math.min(vw / width, vh / STAGE_HEIGHT)
  return { width, scale, left: Math.max(0, (vw - width * scale) / 2), top: Math.max(0, (vh - STAGE_HEIGHT * scale) / 2) }
}

export function FitStage({ children, style, branded = false }: { children: ReactNode; style?: CSSProperties; branded?: boolean }) {
  const [fit, setFit] = useState(() => stageFit(window.innerWidth, window.innerHeight))
  useEffect(() => {
    const onResize = () => setFit(stageFit(window.innerWidth, window.innerHeight))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const cls = `kiosk${branded ? ' kiosk--branded' : ''}`
  if (!fit) return <main className={cls} style={style}>{children}</main>
  return (
    <div className="stage-frame">
      <main
        className={`${cls} kiosk--fit`}
        style={{ ...style, width: fit.width, height: STAGE_HEIGHT, left: fit.left, top: fit.top, transform: `scale(${fit.scale})` }}
      >
        {children}
      </main>
    </div>
  )
}
